/** Shared utilities for dashboard widgets */

export const formatCurrency = (cents: number): string =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(
    cents / 100
  );

export const formatCurrencyShort = (cents: number): string => {
  const dollars = cents / 100;
  if (dollars >= 1_000_000) return `$${(dollars / 1_000_000).toFixed(1)}M`;
  if (dollars >= 1_000) return `$${(dollars / 1_000).toFixed(1)}K`;
  return `$${dollars.toFixed(0)}`;
};

export const formatDate = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const formatTime = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

export const formatRelativeTime = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin} minute${diffMin > 1 ? "s" : ""} ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hour${diffHr > 1 ? "s" : ""} ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay} day${diffDay > 1 ? "s" : ""} ago`;
  const diffWeek = Math.floor(diffDay / 7);
  return `${diffWeek} week${diffWeek > 1 ? "s" : ""} ago`;
};

export const formatHours = (minutes: number): string => {
  const hrs = (minutes / 60).toFixed(1);
  return `${hrs}h`;
};

export const snakeCaseToTitleCase = (s: string): string =>
  s
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

export const practiceAreaColors: Record<string, string> = {
  real_estate: "bg-cyan-100 text-cyan-700",
  family: "bg-pink-100 text-pink-700",
  immigration: "bg-emerald-100 text-emerald-700",
  criminal: "bg-red-100 text-red-700",
  corporate: "bg-purple-100 text-purple-700",
  litigation: "bg-blue-100 text-blue-700",
  wills_estates: "bg-indigo-100 text-indigo-700",
  personal_injury: "bg-orange-100 text-orange-700",
  employment_labour: "bg-amber-100 text-amber-700",
  intellectual_property: "bg-yellow-100 text-yellow-700",
  tax: "bg-lime-100 text-lime-700",
  environmental: "bg-green-100 text-green-700",
  bankruptcy_insolvency: "bg-rose-100 text-rose-700",
  administrative: "bg-slate-100 text-slate-700",
  construction: "bg-stone-100 text-stone-700",
  insurance: "bg-sky-100 text-sky-700",
  entertainment: "bg-fuchsia-100 text-fuchsia-700",
  technology: "bg-violet-100 text-violet-700",
  other: "bg-gray-100 text-gray-700",
};

/** Recharts tooltip style matching the existing design system */
export const tooltipStyle = {
  borderRadius: 12,
  border: "1px solid #e2e8f0",
  boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
};
